import React, { useMemo, useState } from "react";

import { INDUSTRIES, INDUSTRY_CONTENT } from "@/mock";

import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

import { RotateCcw } from "lucide-react";

const arrToLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
const linesToArr = (str) =>
  String(str || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

export const IndustriesCopyEditor = ({ draft, setDraft }) => {
  const [industryKey, setIndustryKey] = useState("medical");

  const defaults = useMemo(() => INDUSTRY_CONTENT[industryKey], [industryKey]);
  const current = useMemo(() => {
    const industries = draft?.industries || {};
    return industries[industryKey] || defaults;
  }, [draft, industryKey, defaults]);

  const updateIndustry = (patch) => {
    setDraft((prev) => {
      const existing = prev?.industries || {};
      const base = existing[industryKey] || defaults;
      return {
        ...(prev || {}),
        industries: {
          ...existing,
          [industryKey]: {
            ...base,
            ...patch,
          },
        },
      };
    });
  };

  return (
    <Card className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <div className="text-sm font-semibold text-foreground">
            Industry switcher content
          </div>
          <p className="mt-2 text-sm text-foreground/60">
            Controls the 3 blocks (use cases, KPIs, and risks) shown on the
            homepage and Industries page.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Select value={industryKey} onValueChange={setIndustryKey}>
            <SelectTrigger className="h-11 w-[220px] rounded-none border-border bg-muted text-foreground">
              <SelectValue placeholder="Industry" />
            </SelectTrigger>
            <SelectContent className="border-border bg-background text-foreground">
              {INDUSTRIES.map((i) => (
                <SelectItem key={i.key} value={i.key}>
                  {i.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="secondary"
            onClick={() =>
              updateIndustry({
                use_cases: defaults.use_cases,
                kpis: defaults.kpis,
                risks: defaults.risks,
              })
            }
            className="h-11 rounded-none border border-border bg-foreground/5 text-foreground hover:bg-foreground hover:text-background"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset
          </Button>
        </div>
      </div>

      <Separator className="my-6 bg-foreground/10" />

      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-foreground/55">
            Top 3 use cases (one per line)
          </div>
          <Textarea
            value={arrToLines(current.use_cases)}
            onChange={(e) => updateIndustry({ use_cases: linesToArr(e.target.value) })}
            className="min-h-[220px] rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
            placeholder="Use case 1\nUse case 2\nUse case 3"
          />
        </div>

        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-foreground/55">
            KPI outcomes (one per line)
          </div>
          <Textarea
            value={arrToLines(current.kpis)}
            onChange={(e) => updateIndustry({ kpis: linesToArr(e.target.value) })}
            className="min-h-[220px] rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
            placeholder="KPI 1\nKPI 2\nKPI 3"
          />
        </div>

        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-foreground/55">
            Risk & security (one per line)
          </div>
          <Textarea
            value={arrToLines(current.risks)}
            onChange={(e) => updateIndustry({ risks: linesToArr(e.target.value) })}
            className="min-h-[220px] rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
            placeholder="Risk 1\nRisk 2\nRisk 3"
          />
        </div>
      </div>

      <div className="mt-4 text-xs text-foreground/45">
        Tip: keep these to 3–5 bullets each for readability.
      </div>
    </Card>
  );
};
